<template>
  <div class="optional-modules-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}{{ clientName ? ` - ${clientName}` : '' }}</h1>
      <button class="btn btn-secondary" @click="handleCancel">
        <i class="ri-close-line"></i>
        返回
      </button>
    </div>

    <div class="content-container">
      <!-- 費用總覽 -->
      <div class="fee-summary-card">
        <h3 class="card-title">費用總覽</h3>
        <div class="fee-breakdown">
          <div class="fee-item">
            <span class="fee-label">方案基礎費用：</span>
            <span class="fee-value">NT$ {{ formatCurrency(feeBreakdown.planAmount) }}</span>
          </div>
          <div class="fee-item">
            <span class="fee-label">選購模組費用：</span>
            <span class="fee-value">NT$ {{ formatCurrency(feeBreakdown.optionalModulesAmount) }}</span>
          </div>
          <div class="fee-item total">
            <span class="fee-label">總月費：</span>
            <span class="fee-value">NT$ {{ formatCurrency(feeBreakdown.totalAmount) }}</span>
          </div>
        </div>
      </div>

      <!-- 選購模組列表 -->
      <div class="modules-card">
        <div class="card-header">
          <h3 class="card-title">已選購模組</h3>
          <button class="btn btn-primary" @click="showAddModal = true">
            <i class="ri-add-line"></i>
            添加模組
          </button>
        </div>

        <div v-if="loading" class="loading-state">
          <i class="ri-loader-4-line spin"></i>
          <span>載入中...</span>
        </div>

        <div v-else-if="modules.length === 0" class="empty-state">
          <i class="ri-inbox-line"></i>
          <p>尚未添加任何選購模組</p>
        </div>

        <div v-else class="modules-list">
          <div 
            v-for="module in modules" 
            :key="module.id" 
            class="module-item"
            :class="{ 'module-cancelled': isModuleCancelled(module) }"
          >
            <div class="module-info">
              <div class="module-header">
                <h4 class="module-name">{{ module.module_name }}</h4>
                <span class="module-code">{{ module.module_code }}</span>
                <span v-if="isModuleCancelled(module)" class="badge badge-warning cancelled-badge">
                  <i class="ri-time-line"></i>
                  已取消訂閱
                </span>
              </div>
              <div class="module-details">
                <div class="detail-item">
                  <span class="detail-label">月費：</span>
                  <span class="detail-value">NT$ {{ formatCurrency(module.monthly_price) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">計費週期：</span>
                  <span class="detail-value">{{ module.billing_cycle === 'monthly' ? '月繳' : '年繳' }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">開始日期：</span>
                  <span class="detail-value">{{ formatDate(module.start_date) }}</span>
                </div>
                <div v-if="module.end_date" class="detail-item">
                  <span class="detail-label">結束日期：</span>
                  <span class="detail-value" :class="{ 'text-warning': isModuleCancelled(module) }">
                    {{ formatDate(module.end_date) }}
                    <span v-if="isModuleCancelled(module)" class="cancelled-hint">
                      （將在此日期失效）
                    </span>
                  </span>
                </div>
                <div v-if="module.notes" class="detail-item">
                  <span class="detail-label">備註：</span>
                  <span class="detail-value">{{ module.notes }}</span>
                </div>
                <div v-if="module.module_code === 'CHATDESK'" class="detail-item seats-detail">
                  <span class="detail-label">座席：</span>
                  <span class="detail-value">
                    內含 {{ module.seats?.includedSeats ?? 3 }} 席
                    ＋ 加購 {{ module.seats?.extraSeats ?? 0 }} 席
                    ＝ 共 {{ module.seats?.totalSeats ?? ((module.seats?.includedSeats ?? 3) + (module.seats?.extraSeats ?? 0)) }} 席
                    <span v-if="(module.seats?.extraSeats ?? 0) > 0" class="seat-fee-hint">
                      （加購月費 NT$ {{ formatCurrency(module.seats?.seatFeeMonthly || (module.seats?.extraSeats || 0) * 250) }}）
                    </span>
                  </span>
                </div>
                <div
                  v-if="module.module_code === 'CHATDESK' && module.seats?.pendingExtraSeats !== null && module.seats?.pendingExtraSeats !== undefined"
                  class="detail-item seats-pending"
                >
                  <span class="detail-label">待生效減席：</span>
                  <span class="detail-value text-warning">
                    加購改為 {{ module.seats.pendingExtraSeats }} 席
                    （總座席 {{ (module.seats.includedSeats || 3) + module.seats.pendingExtraSeats }}）
                    ，預計 {{ formatDate(module.seats.pendingSeatsEffectiveAt) }} 生效；本期不退款
                  </span>
                </div>
              </div>
            </div>
            <div class="module-actions">
              <button
                v-if="module.module_code === 'CHATDESK' && !isModuleCancelled(module)"
                class="btn btn-secondary btn-sm"
                @click="openSeatsModal(module)"
              >
                <i class="ri-user-add-line"></i>
                設定座席
              </button>
              <button 
                class="btn btn-danger btn-sm" 
                @click="handleRemove(module)"
                :disabled="isModuleCancelled(module)"
                :title="isModuleCancelled(module) ? '已取消訂閱的模組無法移除，將在結束日期自動失效' : '移除模組'"
              >
                <i class="ri-delete-bin-line"></i>
                移除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 設定座席 Modal -->
    <div v-if="showSeatsModal" class="modal-overlay" @click="showSeatsModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>設定 ChatDesk 座席</h3>
          <button class="btn-close" @click="showSeatsModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="form-hint seats-intro">
            ChatDesk 內含 3 席客服。加購座席 NT$250／席／月，請輸入加購數量（不含內含席位）。
          </p>
          <p class="form-hint seats-policy">
            加席：立刻生效；人工收款可依本期剩餘天數按比例計費並記錄收款。<br />
            減席：下期（訂閱到期日）生效，本期不退款。
          </p>
          <div class="form-group">
            <label for="extraSeatsEdit" class="required">加購座席數</label>
            <input
              id="extraSeatsEdit"
              v-model.number="seatsForm.extraSeats"
              type="number"
              class="form-control"
              :class="{ 'is-invalid': seatsErrors.extraSeats }"
              min="0"
              step="1"
            />
            <div v-if="seatsErrors.extraSeats" class="invalid-feedback">
              {{ seatsErrors.extraSeats }}
            </div>
            <p class="form-hint">
              目前加購 {{ seatsCurrentExtra }} 席 → 變更為 {{ Number(seatsForm.extraSeats) || 0 }} 席；
              總座席＝{{ 3 + (Number(seatsForm.extraSeats) || 0) }} 席；
              加購月費 NT$ {{ formatCurrency((Number(seatsForm.extraSeats) || 0) * 250) }}
            </p>
          </div>

          <div v-if="seatsChangePreview" class="seats-preview" :class="seatsChangePreview.type">
            <template v-if="seatsChangePreview.type === 'increase'">
              <p class="preview-title">加席預覽（+{{ seatsChangePreview.delta }} 席）</p>
              <p>完整週期加購差額：NT$ {{ formatCurrency(seatsChangePreview.fullCycleAmount) }}</p>
              <p v-if="seatsChangePreview.isProrated">
                本期剩餘約 {{ seatsChangePreview.remainingDays }} 天，按比例應收：
                <strong>NT$ {{ formatCurrency(seatsChangePreview.proratedAmount) }}</strong>
              </p>
              <p v-else-if="seatsChangePreview.proratedAmount > 0">
                本期應收：<strong>NT$ {{ formatCurrency(seatsChangePreview.proratedAmount) }}</strong>
              </p>
              <div
                v-if="clientBillingMethod !== 'self_subscription'"
                class="form-group seats-record-payment"
              >
                <label class="check-row">
                  <input type="checkbox" v-model="seatsForm.recordPayment" />
                  同時記錄人工收款（不延長訂閱）
                </label>
                <div v-if="seatsForm.recordPayment" class="payment-date-row">
                  <label for="seatPaymentDate">收款日期</label>
                  <input
                    id="seatPaymentDate"
                    v-model="seatsForm.paymentDate"
                    type="date"
                    class="form-control"
                    :max="todayDateStr"
                  />
                </div>
              </div>
              <p v-else class="form-hint text-warning">
                自助訂閱客戶加席不會自動調整綠界金額，需另開加購訂單。
              </p>
            </template>
            <template v-else-if="seatsChangePreview.type === 'decrease'">
              <p class="preview-title">減席預覽（−{{ seatsChangePreview.delta }} 席）</p>
              <p>
                將排程至訂閱到期日後生效；本期維持 {{ seatsCurrentExtra }} 加購席、不退款。
                下期起依 {{ Number(seatsForm.extraSeats) || 0 }} 席計費。
              </p>
            </template>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showSeatsModal = false">取消</button>
          <button class="btn btn-primary" @click="handleUpdateSeats" :disabled="seatsSubmitting">
            {{ seatsSubmitting ? '儲存中...' : '儲存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 添加模組 Modal -->
    <div v-if="showAddModal" class="modal-overlay" @click="showAddModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>添加選購模組</h3>
          <button class="btn-close" @click="showAddModal = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="loadingAvailable" class="loading-state">
            <i class="ri-loader-4-line spin"></i>
            <span>載入中...</span>
          </div>
          <div v-else-if="availableModules.length === 0" class="empty-state">
            <p>沒有可選購的模組</p>
          </div>
          <div v-else>
            <div class="form-group">
              <label for="selectedModule" class="required">選擇模組</label>
              <select
                id="selectedModule"
                v-model="addForm.moduleId"
                class="form-control"
                :class="{ 'is-invalid': errors.moduleId }"
                required
              >
                <option value="">請選擇模組</option>
                <option
                  v-for="module in availableModules"
                  :key="module.id"
                  :value="module.id"
                >
                  {{ module.module_name }} ({{ module.module_code }})
                  - 月費: NT$ {{ formatCurrency(module.monthly_price || 0) }} / 年費: NT$ {{ formatCurrency(module.yearly_price || 0) }}
                </option>
              </select>
              <div v-if="errors.moduleId" class="invalid-feedback">
                {{ errors.moduleId }}
              </div>
            </div>

            <div class="form-group">
              <label for="billingCycle">計費週期</label>
              <select
                id="billingCycle"
                v-model="addForm.billingCycle"
                class="form-control"
                :disabled="!!clientBillingCycle"
              >
                <option value="monthly" :disabled="clientBillingCycle === 'yearly'">月繳</option>
                <option value="yearly" :disabled="clientBillingCycle === 'monthly'">年繳</option>
              </select>
              <p v-if="clientBillingCycle" class="form-hint">
                客戶為{{ clientBillingCycle === 'yearly' ? '年繳' : '月繳' }}，選購模組必須使用相同計費週期
              </p>
            </div>

            <div v-if="isAddFormChatDesk" class="form-group">
              <label for="extraSeatsAdd">加購座席數</label>
              <input
                id="extraSeatsAdd"
                v-model.number="addForm.extraSeats"
                type="number"
                class="form-control"
                min="0"
                step="1"
              />
              <p class="form-hint">
                內含 3 席；加購 NT$250／席／月。總座席＝
                {{ 3 + (Number(addForm.extraSeats) || 0) }} 席
              </p>
            </div>

            <div class="form-group">
              <label for="price">{{ addForm.billingCycle === 'yearly' ? '年費' : '月費' }}（可選）</label>
              <input
                id="price"
                v-model.number="addForm.monthlyPrice"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.monthlyPrice }"
                :placeholder="`留空則使用模組預設${addForm.billingCycle === 'yearly' ? '年費' : '月費'}`"
                min="0"
                step="0.01"
              />
              <div v-if="errors.monthlyPrice" class="invalid-feedback">
                {{ errors.monthlyPrice }}
              </div>
              <p class="form-hint">留空則使用模組預設{{ addForm.billingCycle === 'yearly' ? '年費' : '月費' }}</p>
            </div>

            <div class="form-group">
              <label for="startDate">開始日期</label>
              <input
                id="startDate"
                v-model="addForm.startDate"
                type="date"
                class="form-control"
              />
              <p class="form-hint">留空則使用今天</p>
            </div>

            <div class="form-group">
              <label for="endDate">結束日期（可選）</label>
              <input
                id="endDate"
                v-model="addForm.endDate"
                type="date"
                class="form-control"
              />
              <p class="form-hint">留空表示持續計費</p>
            </div>

            <div class="form-group">
              <label for="notes">備註（可選）</label>
              <textarea
                id="notes"
                v-model="addForm.notes"
                class="form-control"
                rows="3"
                placeholder="輸入備註..."
              ></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="showAddModal = false">取消</button>
          <button class="btn btn-primary" @click="handleAdd" :disabled="submitting">
            {{ submitting ? '添加中...' : '確認添加' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js'
import { clientOptionalModuleService } from '@/api/modules/clientOptionalModules'
import { clientMonthlyFeeService } from '@/api/modules/clientMonthlyFee'
import { clientService } from '@/api/modules/clients'
import { showError, showSuccess } from '@/utils/swal'

export default {
  name: 'OptionalModules',
  setup() {
    const { pageTitle: baseTitle } = usePageTitle('選購模組管理')
    return {
      baseTitle
    }
  },
  computed: {
    pageTitle() {
      return this.baseTitle + (this.clientName ? ` - ${this.clientName}` : '')
    },
    isAddFormChatDesk() {
      return this.selectedModuleData?.module_code === 'CHATDESK'
    },
    seatsCurrentExtra() {
      return this.seatsTargetModule?.seats?.extraSeats ?? 0
    },
    todayDateStr() {
      return new Date().toISOString().slice(0, 10)
    },
    seatsChangePreview() {
      const next = Number(this.seatsForm.extraSeats)
      if (!Number.isFinite(next) || next < 0 || !Number.isInteger(next)) return null
      const current = this.seatsCurrentExtra
      if (next === current) return null

      const billingCycle = this.seatsTargetModule?.billing_cycle || this.clientBillingCycle || 'monthly'
      const fullCycleDays = billingCycle === 'yearly' ? 365 : 30
      const delta = Math.abs(next - current)

      if (next > current) {
        const fullCycleAmount = delta * 250 * (billingCycle === 'yearly' ? 12 : 1)
        let remainingDays = null
        let proratedAmount = fullCycleAmount
        let isProrated = false
        if (this.clientSubscriptionEndDate) {
          const start = new Date()
          start.setHours(0, 0, 0, 0)
          const end = new Date(this.clientSubscriptionEndDate)
          end.setHours(0, 0, 0, 0)
          remainingDays = Math.max(0, Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1)
          if (remainingDays <= 0) {
            proratedAmount = 0
          } else if (remainingDays < fullCycleDays) {
            isProrated = true
            proratedAmount = Math.round((remainingDays / fullCycleDays) * fullCycleAmount * 100) / 100
          }
        }
        return {
          type: 'increase',
          delta,
          fullCycleAmount,
          proratedAmount,
          remainingDays,
          isProrated
        }
      }

      return { type: 'decrease', delta }
    }
  },
  data() {
    return {
      clientId: null,
      clientName: '',
      clientBillingCycle: null, // 客戶的計費週期
      clientSubscriptionEndDate: null,
      clientBillingMethod: null,
      modules: [],
      feeBreakdown: {
        planAmount: 0,
        optionalModulesAmount: 0,
        totalAmount: 0,
        optionalModulesDetail: []
      },
      availableModules: [],
      loading: false,
      loadingAvailable: false,
      submitting: false,
      showAddModal: false,
      showSeatsModal: false,
      seatsSubmitting: false,
      seatsTargetModule: null,
      seatsForm: {
        extraSeats: 0,
        recordPayment: true,
        paymentDate: ''
      },
      seatsErrors: {},
      addForm: {
        moduleId: '',
        monthlyPrice: null,
        billingCycle: 'monthly',
        startDate: '',
        endDate: '',
        notes: '',
        extraSeats: 0
      },
      errors: {},
      selectedModuleData: null
    }
  },
  mounted() {
    this.clientId = this.$route.params.id || this.$route.params.clientId
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        await Promise.all([
          this.loadModules(),
          this.loadFeeBreakdown(),
          this.loadClientInfo()
        ])
      } catch (error) {
        const errorMessage = error?.message || 
                            (error?.response?.data?.message) || 
                            '載入資料失敗，請稍後再試'
        await showError('載入失敗', errorMessage)
      } finally {
        this.loading = false
      }
    },
    async loadClientInfo() {
      try {
        const response = await clientService.getClient(this.clientId)
        const client = response?.data?.clientData
          || response?.data?.client
          || response?.data
          || null
        if (!client) return
        this.clientName = client.clientName || client.client_name || this.clientName
        this.clientSubscriptionEndDate = client.subscriptionEndDate || client.subscription_end_date || null
        this.clientBillingMethod = client.billingMethod || client.billing_method || null
      } catch (error) {
        console.error('載入客戶資訊失敗：', error)
      }
    },
    async loadModules() {
      try {
        const response = await clientOptionalModuleService.getClientOptionalModules(this.clientId)
        if (response?.success && response?.data) {
          this.modules = response.data.modules || []
          // 取得客戶名稱（從第一個模組或從路由參數）
          if (this.modules.length > 0 && !this.clientName) {
            // 可以從其他地方取得客戶名稱
          }
        }
      } catch (error) {
        // 如果載入失敗，設為空陣列，避免頁面崩潰
        this.modules = []
        console.error('載入選購模組失敗：', error)
        // 不 throw，讓頁面可以繼續載入其他資料
      }
    },
    async loadFeeBreakdown() {
      try {
        const response = await clientMonthlyFeeService.calculateMonthlyFee(this.clientId)
        if (response?.success && response?.data) {
          this.feeBreakdown = response.data
          // 保存客戶的計費週期
          if (response.data.billingCycle) {
            this.clientBillingCycle = response.data.billingCycle
            // 如果客戶有計費週期，自動設置表單的計費週期
            if (!this.addForm.moduleId) {
              this.addForm.billingCycle = response.data.billingCycle
            }
          }
        }
      } catch (error) {
        throw error
      }
    },
    async loadAvailableModules() {
      this.loadingAvailable = true
      try {
        const response = await clientOptionalModuleService.getAvailableOptionalModules(this.clientId)
        if (response?.success && response?.data) {
          this.availableModules = response.data.modules || []
        }
      } catch (error) {
        const errorMessage = error?.message || 
                            (error?.response?.data?.message) || 
                            '載入可選購模組失敗，請稍後再試'
        await showError('載入失敗', errorMessage)
      } finally {
        this.loadingAvailable = false
      }
    },
    async handleAdd() {
      this.errors = {}
      
      if (!this.addForm.moduleId) {
        this.errors.moduleId = '請選擇模組'
        return
      }

      this.submitting = true
      try {
        const moduleData = {
          moduleId: this.addForm.moduleId,
          billingCycle: this.addForm.billingCycle,
          notes: this.addForm.notes || undefined
        }

        if (this.addForm.monthlyPrice && this.addForm.monthlyPrice > 0) {
          moduleData.monthlyPrice = this.addForm.monthlyPrice
        }

        if (this.addForm.startDate) {
          moduleData.startDate = this.addForm.startDate
        }

        if (this.addForm.endDate) {
          moduleData.endDate = this.addForm.endDate
        }

        if (this.isAddFormChatDesk) {
          const seats = Number(this.addForm.extraSeats)
          moduleData.extraSeats = Number.isFinite(seats) && seats >= 0 ? Math.floor(seats) : 0
        }

        const response = await clientOptionalModuleService.addOptionalModule(this.clientId, moduleData)
        
        // 檢查是否有按比例計算資訊
        if (response?.data?.module?.proratedInfo?.isProrated) {
          const proratedInfo = response.data.module.proratedInfo
          const message = `選購模組已成功添加\n\n` +
            `按比例計算費用：\n` +
            `- 完整週期費用：NT$ ${this.formatCurrency(proratedInfo.fullCycleAmount)}\n` +
            `- 實際天數：${proratedInfo.proratedDays} 天（不足一個完整週期）\n` +
            `- 按比例費用：NT$ ${this.formatCurrency(proratedInfo.proratedAmount)}\n` +
            `- 下個計費週期結束日期：${this.formatDate(proratedInfo.nextBillingEndDate)}`
          await showSuccess('添加成功', message)
        } else {
          await showSuccess('添加成功', '選購模組已成功添加')
        }
        
        this.showAddModal = false
        this.resetAddForm()
        await this.loadData()
      } catch (error) {
        // axios 攔截器已經將錯誤訊息提取到 error.message
        // 安全地獲取錯誤訊息，避免訪問 undefined 屬性
        let errorMessage = '添加選購模組失敗，請稍後再試'
        
        if (error && typeof error === 'object') {
          // 優先使用 error.message（axios 攔截器已提取）
          if (error.message && typeof error.message === 'string') {
            errorMessage = error.message
          } 
          // 其次嘗試 error.data.message
          else if (error.data && error.data.message && typeof error.data.message === 'string') {
            errorMessage = error.data.message
          }
          // 最後嘗試 error.response.data.message
          else if (error.response && error.response.data && error.response.data.message && typeof error.response.data.message === 'string') {
            errorMessage = error.response.data.message
          }
        }
        
        await showError('添加失敗', errorMessage)
        console.error('添加選購模組錯誤：', error)
      } finally {
        this.submitting = false
      }
    },
    async handleRemove(module) {
      if (!confirm(`確定要移除「${module.module_name}」模組嗎？`)) {
        return
      }

      try {
        await clientOptionalModuleService.removeOptionalModule(this.clientId, module.module_id)
        await showSuccess('移除成功', '選購模組已成功移除')
        await this.loadData()
      } catch (error) {
        const errorMessage = error?.message || 
                            (error?.response?.data?.message) || 
                            '移除選購模組失敗，請稍後再試'
        await showError('添加失敗', errorMessage)
      }
    },
    resetAddForm() {
      this.addForm = {
        moduleId: '',
        monthlyPrice: null,
        billingCycle: this.clientBillingCycle || 'monthly',
        startDate: '',
        endDate: '',
        notes: '',
        extraSeats: 0
      }
      this.selectedModuleData = null
      this.errors = {}
    },
    openSeatsModal(module) {
      this.seatsTargetModule = module
      const isManual = this.clientBillingMethod !== 'self_subscription'
      this.seatsForm = {
        extraSeats: module.seats?.extraSeats ?? 0,
        recordPayment: isManual,
        paymentDate: this.todayDateStr
      }
      this.seatsErrors = {}
      this.showSeatsModal = true
    },
    async handleUpdateSeats() {
      this.seatsErrors = {}
      const seats = Number(this.seatsForm.extraSeats)
      if (!Number.isFinite(seats) || seats < 0 || !Number.isInteger(seats)) {
        this.seatsErrors.extraSeats = '請輸入 0 或正整數'
        return
      }
      if (!this.seatsTargetModule) return

      const isIncrease = seats > this.seatsCurrentExtra
      const options = {}
      if (isIncrease && this.seatsForm.recordPayment && this.clientBillingMethod !== 'self_subscription') {
        options.recordPayment = true
        options.paymentDate = this.seatsForm.paymentDate || this.todayDateStr
      }

      this.seatsSubmitting = true
      try {
        const response = await clientOptionalModuleService.updateChatDeskSeats(
          this.clientId,
          this.seatsTargetModule.module_id,
          seats,
          options
        )
        const result = response?.data?.module || response?.data || {}
        let message = response?.message || `已設定加購 ${seats} 席（總座席 ${3 + seats}）`

        if (result.changeType === 'increase' && result.proratedInfo) {
          const p = result.proratedInfo
          message = `已加席至 ${seats}（總座席 ${3 + seats}）\n\n` +
            `差額完整週期：NT$ ${this.formatCurrency(p.fullCycleAmount)}\n` +
            (p.isProrated
              ? `本期剩餘 ${p.proratedDays} 天，按比例應收：NT$ ${this.formatCurrency(p.proratedAmount)}\n`
              : `本期應收：NT$ ${this.formatCurrency(p.proratedAmount)}\n`) +
            (result.paymentRecord
              ? '已同時記錄人工收款（未延長訂閱）。'
              : '尚未記錄收款，可至付款記錄／訂閱頁補登。')
        } else if (result.changeType === 'decrease' && result.decreaseSchedule?.message) {
          message = result.decreaseSchedule.message
        }

        if (result.ecpayBillingNote) {
          message += `\n\n注意：${result.ecpayBillingNote}`
        }

        await showSuccess('儲存成功', message)
        this.showSeatsModal = false
        await this.loadData()
      } catch (error) {
        const errorMessage = error?.message
          || error?.response?.data?.message
          || '更新座席失敗，請稍後再試'
        await showError('儲存失敗', errorMessage)
      } finally {
        this.seatsSubmitting = false
      }
    },
    handleCancel() {
      this.$router.push({ name: 'EditClient', params: { id: this.clientId } })
    },
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '0.00'
      return parseFloat(amount).toLocaleString('zh-TW', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    },
    isModuleCancelled(module) {
      // 判斷模組是否已取消訂閱：有 end_date 且未過期
      if (!module.end_date) return false
      const endDate = new Date(module.end_date)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return endDate >= today
    },
    updatePriceFromModule() {
      if (!this.selectedModuleData) {
        this.addForm.monthlyPrice = null
        return
      }
      
      if (this.addForm.billingCycle === 'yearly') {
        // 年繳：帶入年費
        this.addForm.monthlyPrice = this.selectedModuleData.yearly_price || null
      } else {
        // 月繳：帶入月費
        this.addForm.monthlyPrice = this.selectedModuleData.monthly_price || null
      }
    }
  },
  watch: {
    showAddModal(newVal) {
      if (newVal) {
        this.loadAvailableModules()
        this.resetAddForm()
      }
    },
    'addForm.moduleId'(newModuleId) {
      if (newModuleId) {
        // 找到選中的模組資料
        this.selectedModuleData = this.availableModules.find(m => m.id === newModuleId) || null
        // 根據計費週期自動帶入對應的預設價格
        this.updatePriceFromModule()
      } else {
        this.selectedModuleData = null
        this.addForm.monthlyPrice = null
      }
    },
    'addForm.billingCycle'() {
      // 當計費週期改變時，重新帶入對應的預設價格
      this.updatePriceFromModule()
    }
  }
}
</script>

<style scoped>
.optional-modules-container {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.fee-summary-card,
.modules-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.fee-breakdown {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.fee-item {
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
}

.fee-item.total {
  background: #e3f2fd;
  font-weight: 600;
  font-size: 16px;
}

.fee-label {
  color: #666;
}

.fee-value {
  color: #333;
  font-weight: 500;
}

.modules-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.module-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fafafa;
  transition: all 0.2s;
}

.module-item.module-cancelled {
  border-color: #ff9800;
  background: #fff8e1;
  opacity: 0.9;
}

.module-info {
  flex: 1;
}

.module-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.module-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.module-code {
  font-size: 12px;
  color: #666;
  background: #e0e0e0;
  padding: 2px 8px;
  border-radius: 4px;
}

.cancelled-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffc107;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  margin-left: 8px;
}

.cancelled-badge i {
  font-size: 14px;
}

.cancelled-hint {
  font-size: 12px;
  color: #ff9800;
  margin-left: 4px;
  font-style: italic;
}

.text-warning {
  color: #ff9800;
  font-weight: 500;
}

.module-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 8px;
}

.detail-item {
  display: flex;
  gap: 8px;
}

.detail-label {
  color: #666;
  font-size: 14px;
}

.detail-value {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.module-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
}

.seat-fee-hint {
  color: #666;
  font-weight: 400;
  font-size: 13px;
}

.seats-intro {
  margin-bottom: 8px;
}

.seats-policy {
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 6px;
  line-height: 1.5;
}

.seats-preview {
  margin-top: 12px;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  font-size: 14px;
}

.seats-preview.increase {
  border-color: #bfdbfe;
  background: #eff6ff;
}

.seats-preview.decrease {
  border-color: #fde68a;
  background: #fffbeb;
}

.seats-preview .preview-title {
  font-weight: 600;
  margin: 0 0 8px;
}

.seats-record-payment {
  margin-top: 12px;
}

.seats-record-payment .check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.payment-date-row {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 220px;
}

.seats-pending {
  margin-top: 4px;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 48px;
  color: #999;
}

.loading-state .spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #e0e0e0;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
}

.form-group label.required::after {
  content: ' *';
  color: #f44336;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-control.is-invalid {
  border-color: #f44336;
}

.invalid-feedback {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: #1976d2;
  color: white;
}

.btn-primary:hover {
  background: #1565c0;
}

.btn-secondary {
  background: #757575;
  color: white;
}

.btn-secondary:hover {
  background: #616161;
}

.btn-danger {
  background: #f44336;
  color: white;
}

.btn-danger:hover {
  background: #d32f2f;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
